import { useEffect } from 'react'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Autocomplete, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adjustStock, type StockStatus } from '../../api/stocuri'
import { listArticles } from '../../api/nomenclatoare'
import { listWarehouses, getWarehouse } from '../../api/depozite'
import { errorMessage } from '../../api/client'

const schema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  warehouseId: z.string().min(1, 'Selectați un depozit'),
  zoneId: z.string().nullable(),
  locationId: z.string().nullable(),
  lotNumber: z.string().optional(),
  status: z.enum(['Liber', 'Blocat', 'InControl', 'Scrap']),
  quantity: z.string().min(1, 'Câmp obligatoriu'),
  reason: z.string().min(1, 'Motivul este obligatoriu'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { articleId: '', warehouseId: '', zoneId: null, locationId: null, lotNumber: '', status: 'Liber', quantity: '', reason: '' }

export default function StockAdjustmentDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses, enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  useEffect(() => { if (open) reset(EMPTY) }, [open, reset])
  const warehouseId = useWatch({ control, name: 'warehouseId' })
  const { data: warehouseDetail } = useQuery({ queryKey: ['stocuri', 'depozit', warehouseId || null], queryFn: () => getWarehouse(warehouseId), enabled: !!warehouseId })
  const zoneId = useWatch({ control, name: 'zoneId' })
  const selectedZone = warehouseDetail?.zones.find((z) => z.id === zoneId)

  const mutation = useMutation({
    mutationFn: (values: FormValues) => adjustStock({
      articleId: values.articleId, warehouseId: values.warehouseId, zoneId: values.zoneId, locationId: values.locationId,
      lotNumber: values.lotNumber || null, status: values.status as StockStatus, quantity: Number(values.quantity), reason: values.reason,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stocuri'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Ajustare manuală de stoc</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="articleId" control={control} render={({ field }) => (
              <Autocomplete
                options={articles?.map((a) => a.id) ?? []}
                getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.articleId} helperText={errors.articleId?.message} />}
              />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="warehouseId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={warehouses?.map((w) => w.id) ?? []}
                  getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Depozit" error={!!errors.warehouseId} helperText={errors.warehouseId?.message} />}
                />
              )} />
              <Controller name="zoneId" control={control} render={({ field }) => (
                <TextField {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)}
                  select label="Zonă" sx={{ width: 160 }}>
                  <MenuItem value="">—</MenuItem>
                  {warehouseDetail?.zones.map((z) => <MenuItem key={z.id} value={z.id!}>{z.name}</MenuItem>)}
                </TextField>
              )} />
              <Controller name="locationId" control={control} render={({ field }) => (
                <TextField {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)}
                  select label="Locație" sx={{ width: 140 }} disabled={!selectedZone}>
                  <MenuItem value="">—</MenuItem>
                  {selectedZone?.locations.map((l) => <MenuItem key={l.id} value={l.id!}>{l.code}</MenuItem>)}
                </TextField>
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="lotNumber" control={control} render={({ field }) => (
                <TextField {...field} label="Lot (opțional)" sx={{ flexGrow: 1 }} />
              )} />
              <Controller name="status" control={control} render={({ field }) => (
                <TextField {...field} select label="Status" sx={{ width: 140 }} error={!!errors.status} helperText={errors.status?.message}>
                  <MenuItem value="Liber">Liber</MenuItem>
                  <MenuItem value="Blocat">Blocat</MenuItem>
                  <MenuItem value="InControl">În control</MenuItem>
                  <MenuItem value="Scrap">Scrap</MenuItem>
                </TextField>
              )} />
              <Controller name="quantity" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Cantitate (± )" sx={{ width: 160 }}
                  error={!!errors.quantity} helperText={errors.quantity?.message ?? 'Pozitiv = intrare, negativ = ieșire'} />
              )} />
            </Stack>
            <Controller name="reason" control={control} render={({ field }) => (
              <TextField {...field} label="Motiv" fullWidth multiline minRows={2} error={!!errors.reason} helperText={errors.reason?.message} />
            )} />
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
